import React from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { useTranslations } from 'use-intl';
import {
	useRequestCreditBureauReportMutation,
	RequestCreditBureauReportMutation
} from '@/graphql/__generated__/onboarding';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';

const CreditBureauModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	clientId: string;
	onSuccess?: () => void;
}> = ({ isOpen, onClose, clientId, onSuccess }) => {
	const t = useTranslations('CreditBureau');
	const accountsClient = useApolloClient(SCHEMAS.ONBOARDING);

	const [requestCreditBureauReport, { loading }] = useRequestCreditBureauReportMutation({
		client: accountsClient,
		onCompleted: async (data: RequestCreditBureauReportMutation) => {
			const result = data.requestCreditBureauReport;
			if (result?.successful) {
				logger.success('Credit bureau report requested successfully');
				toast.success(t('ReportRequestedSuccessfully'));
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (result?.mutationErrors && result.mutationErrors.length > 0) {
				result.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								toast.error(error.message);
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								toast.error(error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								toast.error(error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								toast.error(t('ErrorRequestingReport'));
								break;
						}
					}
				});
			}
		},
		onError: (error) => {
			logger.error('Error requesting credit bureau report', error);
			toast.error(t('ErrorRequestingReport'));
		}
	});

	const handleRequestReport = async () => {
		await requestCreditBureauReport({
			variables: {
				input: {
					clientId
				}
			}
		});
	};

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('RequestReport')}
			title={t('RequestCreditBureauReport')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleRequestReport}
			maxWidth={600}
			subtitle={t('RequestCreditBureauReportSubtitle')}
			confirmBtnLoading={loading}
		>
			<div className="flex flex-col gap-4 py-4">{t('RequestCreditBureauReportDescription')}</div>
		</ConfirmModal>
	);
};

export default CreditBureauModal;
